import { useCallback, useEffect, useState } from 'react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { pushSupport, urlBase64ToUint8Array } from '../lib/push';

/** The state a UI needs to explain itself.
 *
 *  Distinguishing `blocked` from `off` matters: a browser permission that was denied
 *  cannot be re-requested from script — the user has to change it in browser
 *  settings — so offering an "Enable" button there is a dead end that looks broken.
 *
 *  `unconfigured` means the server has no VAPID keys, which is a deployment state,
 *  not something the user can act on.
 *
 *  `noworker` means the browser supports push but no service worker is registered on
 *  this origin, so there is nothing to attach a subscription to. Transient in
 *  production (registration happens on load) and permanent in dev, where the worker is
 *  deliberately off — it used to break the login POST. */
export type PushState =
  | 'loading'
  | 'unsupported'
  | 'insecure'
  | 'unconfigured'
  | 'noworker'
  | 'blocked'
  | 'off'
  | 'on';

/** Subscribe/unsubscribe this device for Web Push.
 *
 *  Per DEVICE, not per account: the subscription belongs to this browser on this
 *  machine, so the same person enabling notifications on a phone and a laptop
 *  produces two rows. Whoever is logged in at subscribe time owns them — that is why
 *  the server upserts by endpoint rather than creating blindly.
 *
 *  Used by both shells. A platform admin needs this as much as a customer: they get
 *  their own notifications (failed provisioning, sign-ups awaiting approval), which
 *  is what `MessageAudience.PLATFORM_ADMIN` exists for. */
export function usePushNotifications() {
  const crud = useClientQueries(schema);
  const { data: publicKey, isLoading: keyLoading } = crud.$procs.pushPublicKey.useQuery();
  const subscribe = crud.$procs.pushSubscribe.useMutation();
  const unsubscribe = crud.$procs.pushUnsubscribe.useMutation();
  const test = crud.$procs.pushTest.useMutation();

  const [state, setState] = useState<PushState>('loading');
  const [busy, setBusy] = useState(false);

  // Read the real state from the browser rather than tracking it ourselves: the
  // permission can be revoked from browser settings and the subscription can expire,
  // neither of which notifies the page. Whatever the browser says now is the truth.
  const refresh = useCallback(async () => {
    const support = pushSupport();
    if (!support.supported) {
      setState(support.reason === 'insecure' ? 'insecure' : 'unsupported');
      return;
    }
    if (keyLoading) return;
    if (!publicKey) {
      setState('unconfigured');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('blocked');
      return;
    }
    // `getRegistration()` rather than `ready`: `ready` waits for a worker to become
    // active and NEVER settles when none is registered, so in dev — where the worker is
    // deliberately off — this promise hung, setState was never reached, and the toggle
    // stayed at 'loading' forever. The UI's answer to 'loading' is to render nothing,
    // which is how the settings row ended up as a label with no control next to it.
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      setState('noworker');
      return;
    }
    const existing = await reg.pushManager.getSubscription();
    setState(existing ? 'on' : 'off');
  }, [keyLoading, publicKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Must be called from a user gesture — browsers refuse a permission prompt that
   *  is not tied to a click. */
  const enable = useCallback(async () => {
    if (!publicKey) return;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'blocked' : 'off');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      // Reuse an existing subscription if the browser still has one: subscribing
      // twice with the same key returns the same endpoint anyway, and re-sending it
      // keeps the server row pointed at the current user.
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          // Required by Chrome: promises that every push produces a visible
          // notification. Our worker honours it — it always shows one, even for a
          // malformed payload.
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));

      // `toJSON()` gives the endpoint plus the two per-device encryption keys the
      // server needs. These are NOT the VAPID pair — they encrypt the payload so the
      // push service cannot read it.
      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
        throw new Error('subscription missing endpoint or keys');
      }

      await subscribe.mutateAsync({
        args: {
          input: {
            endpoint: json.endpoint,
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
            userAgent: navigator.userAgent,
          },
        },
      });
      setState('on');
    } finally {
      setBusy(false);
    }
  }, [publicKey, subscribe]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        // Tell the server first. If the order were reversed and the request failed,
        // the row would linger and the server would keep pushing to an endpoint the
        // browser has already dropped — the push service would answer 410 and the
        // row would eventually be pruned, but only after wasted sends.
        await unsubscribe.mutateAsync({ args: { input: { endpoint: sub.endpoint } } });
        await sub.unsubscribe();
      }
      setState('off');
    } finally {
      setBusy(false);
    }
  }, [unsubscribe]);

  /** Fire a test push to this user's own devices. It is a real end-to-end send that
   *  goes through the real `push/requested` sink — so success proves the whole chain,
   *  not just that the button works. Re-throws so the caller can toast on failure. */
  const sendTest = useCallback(async () => {
    setBusy(true);
    try {
      await test.mutateAsync({});
    } finally {
      setBusy(false);
    }
  }, [test]);

  return { state, busy, enable, disable, refresh, sendTest };
}
